import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import {
  DEFAULT_NUMBERING,
  NumberingTokenMissingError,
  renderDocumentNumber,
  sequenceScopeKey,
  tokensOf,
  type NumberedDocumentType,
  type NumberingConfig,
  type NumberingTokenValues
} from "../../domain/settings/document-numbering.js";
import type { SystemSettingsRepository } from "../../repositories/system-settings-repository.js";
import type { SystemSettingsProvider } from "./system-settings-provider.js";

export interface DocumentNumberRequest {
  readonly documentType: NumberedDocumentType;
  readonly clientId?: string | null;
  readonly projectId?: string | null;
  readonly unitAreaId?: string | null;
  readonly revision?: string | null;
  readonly osNumber?: string | null;
  readonly subOsNumber?: string | null;
  /**
   * Quantos documentos o escopo já tinha no FORMATO HISTÓRICO antes do
   * contador existir. Só é usado quando o modelo configurado é o padrão:
   * num formato novo a contagem começa do zero.
   */
  readonly legacyCount: () => Promise<number>;
  /**
   * O número já pertence a um documento? (última barreira contra
   * duplicidade). `perClient`: o número só precisa ser único dentro do
   * Cliente (contagem por Cliente) — senão, em todo o sistema.
   */
  readonly isTaken: (candidate: string, scope: { perClient: boolean; perProject: boolean }) => Promise<boolean>;
  readonly now?: Date;
}

/** Porta que os serviços de documento recebem — só aloca, nada mais. */
export interface DocumentNumberAllocator {
  allocate(request: DocumentNumberRequest): Promise<string>;
}

export interface NumberingCodeResolver {
  clientCode(clientId: string): Promise<string | null>;
  projectCode(projectId: string): Promise<string | null>;
  unitCode(unitAreaId: string): Promise<string | null>;
}

const MAX_ATTEMPTS = 200;

function isDefaultFormat(config: NumberingConfig): boolean {
  const d = DEFAULT_NUMBERING[config.documentType];
  return config.template === d.template && config.digits === d.digits && (config.prefix ?? "") === d.prefix && (config.suffix ?? "") === d.suffix;
}

/**
 * Aloca números de documento pelo modelo de CONFIGURAÇÕES → NUMERAÇÃO.
 *
 * Por que é seguro com várias pessoas criando ao mesmo tempo:
 *  1. o sequencial vem de `nextSequence`, um incremento atômico no
 *     armazenamento (no SQLite, um único INSERT … ON CONFLICT … RETURNING) —
 *     duas criações nunca recebem o mesmo valor;
 *  2. antes de devolver, o número é conferido contra os documentos que já
 *     existem, e um já usado (documento criado antes do contador, importação,
 *     modelo alterado e depois revertido) faz avançar para o próximo.
 *
 * Interno: os serviços de documento chamam; não é exposto ao navegador.
 */
export class DocumentNumberingService implements DocumentNumberAllocator {
  private resolver: NumberingCodeResolver | null = null;

  constructor(private readonly provider: SystemSettingsProvider, private readonly repo: SystemSettingsRepository) {}

  attachCodeResolver(resolver: NumberingCodeResolver): void {
    this.resolver = resolver;
  }

  async allocate(request: DocumentNumberRequest): Promise<string> {
    const settings = await this.provider.get();
    const config = settings.numbering[request.documentType] ?? DEFAULT_NUMBERING[request.documentType];
    const now = request.now ?? new Date();
    const values = await this.tokenValues(config, request, now);

    try {
      // Falta de dado para um token recusa ANTES de consumir um número da sequência.
      renderDocumentNumber(config, values, 1);
    } catch (e) {
      if (e instanceof NumberingTokenMissingError) throw new AppError(ErrorCode.Validation, "Numbering token missing", { safeMessage: e.message });
      throw e;
    }

    const scopeKey = sequenceScopeKey(config, values);
    const seed = isDefaultFormat(config) ? Math.max(0, await request.legacyCount()) : 0;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      const sequence = await this.repo.nextSequence(scopeKey, seed);
      const candidate = renderDocumentNumber(config, values, sequence);
      if (!(await request.isTaken(candidate, { perClient: config.resetPerClient, perProject: config.resetPerProject }))) return candidate;
    }
    throw new AppError(ErrorCode.Conflict, "Numbering exhausted", {
      safeMessage: "Não foi possível gerar um número de documento livre. Verifique o modelo de numeração em Configurações → Numeração."
    });
  }

  private async tokenValues(config: NumberingConfig, request: DocumentNumberRequest, now: Date): Promise<NumberingTokenValues> {
    const used = new Set(tokensOf(config.template));
    const resolve = async (token: string, id: string | null | undefined, fn: (r: NumberingCodeResolver, id: string) => Promise<string | null>) =>
      used.has(token) && id && this.resolver ? await fn(this.resolver, id).catch(() => null) : null;
    return {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      revision: request.revision ?? "00",
      clientId: request.clientId ?? null,
      clientCode: await resolve("{CLIENTE}", request.clientId, (r, id) => r.clientCode(id)),
      projectId: request.projectId ?? null,
      projectCode: await resolve("{PROJETO}", request.projectId, (r, id) => r.projectCode(id)),
      unitCode: await resolve("{UNIDADE}", request.unitAreaId, (r, id) => r.unitCode(id)),
      osNumber: request.osNumber ?? null,
      subOsNumber: request.subOsNumber ?? null
    };
  }
}
